import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Edit3, X, Save, Tag, Loader2, BarChart3, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPromoCodesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: '', discount_percent: 10, description: '', active: true, max_uses: '', expires_at: '', broadcast: false });
  const [busyBroadcast, setBusyBroadcast] = useState(null);

  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/');
  }, [user, navigate]);

  const fetchCodes = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/promo-codes`, { withCredentials: true });
      setCodes(data.promo_codes || []);
    } catch {
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) fetchCodes(); }, [user]);

  const startCreate = () => {
    setForm({ code: '', discount_percent: 10, description: '', active: true, max_uses: '', expires_at: '', broadcast: false });
    setCreating(true);
    setEditing(null);
  };

  const startEdit = (c) => {
    setForm({
      code: c.code,
      discount_percent: c.discount_percent,
      description: c.description || '',
      active: !!c.active,
      max_uses: c.max_uses == null ? '' : String(c.max_uses),
      expires_at: c.expires_at || '',
      broadcast: false,
    });
    setEditing(c.code);
    setCreating(false);
  };

  const cancel = () => { setEditing(null); setCreating(false); };

  const handleSave = async () => {
    if (!form.code.trim()) { toast.error('Code is required'); return; }
    const dp = Number(form.discount_percent);
    if (!Number.isFinite(dp) || dp < 1 || dp > 100) {
      toast.error('Discount must be between 1 and 100');
      return;
    }
    const payload = {
      code: form.code.toUpperCase().trim(),
      discount_percent: dp,
      description: form.description,
      active: form.active,
      max_uses: form.max_uses === '' ? null : Number(form.max_uses),
      expires_at: form.expires_at || null,
    };
    try {
      if (creating) {
        await axios.post(`${API}/admin/promo-codes`, payload, { withCredentials: true });
        toast.success('Promo code created');
        if (form.broadcast) {
          try {
            const { data: bcast } = await axios.post(
              `${API}/admin/promo-codes/${payload.code}/broadcast`,
              {},
              { withCredentials: true },
            );
            toast.success(`Sent to ${bcast.sent} subscriber(s)`);
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Broadcast failed');
          }
        }
      } else {
        const { code: _ignored, ...patch } = payload;
        await axios.patch(`${API}/admin/promo-codes/${editing}`, patch, { withCredentials: true });
        toast.success('Promo code updated');
      }
      cancel();
      fetchCodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleBroadcast = async (code) => {
    if (!window.confirm(`Email code ${code} to every customer who opted in to marketing emails?`)) return;
    setBusyBroadcast(code);
    try {
      const { data } = await axios.post(`${API}/admin/promo-codes/${code}/broadcast`, {}, { withCredentials: true });
      toast.success(`Sent to ${data.sent} subscriber(s) (of ${data.total_subscribers})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Broadcast failed');
    } finally { setBusyBroadcast(null); }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete promo code ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${code}`, { withCredentials: true });
      toast.success('Deleted');
      fetchCodes();
    } catch {
      toast.error('Delete failed');
    }
  };

  // Group every auto-generated CART-XXXX entry into a single synthetic row
  // so the table doesn't drown in recovery codes. The synthetic row is
  // rendered first; clicking its Reports link surfaces the aggregate report.
  const visibleRows = useMemo(() => {
    const auto = codes.filter((c) => c.auto_generated);
    const manual = codes.filter((c) => !c.auto_generated);
    if (!auto.length) return manual;
    const totalUses = auto.reduce((s, c) => s + (c.times_used || 0), 0);
    const synthetic = {
      _synthetic: true,
      code: 'CART_RECOVERY',
      discount_percent: auto[0].discount_percent || 10,
      description: `Auto-generated cart-recovery codes (${auto.length} issued)`,
      active: true,
      times_used: totalUses,
      max_uses: null,
      expires_at: null,
    };
    return [synthetic, ...manual];
  }, [codes]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="admin-promo-codes-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl tracking-tight font-light text-foreground font-heading flex items-center gap-2"><Tag className="h-6 w-6" /> Promo Codes</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">Create, edit and disable discount codes used at checkout.</p>
        </div>
        <Button onClick={startCreate} disabled={creating || editing} className="bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" data-testid="create-promo-button">
          <Plus className="h-4 w-4 mr-1" /> New Code
        </Button>
      </div>

      {(creating || editing) && (
        <Card className="mb-6 border-[hsl(210,45%,40%)]/40">
          <CardHeader>
            <CardTitle className="font-heading text-lg">{creating ? 'Create Promo Code' : `Edit ${editing}`}</CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs font-body">Code</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                disabled={!creating}
                placeholder="SUMMER10"
                data-testid="promo-code-field"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-body">Discount %</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={form.discount_percent}
                onChange={(e) => setForm((p) => ({ ...p, discount_percent: e.target.value }))}
                data-testid="promo-discount-field"
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs font-body">Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="e.g. Summer 2026 promo"
                data-testid="promo-description-field"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-body">Max uses (blank = unlimited)</Label>
              <Input
                type="number"
                min={1}
                value={form.max_uses}
                onChange={(e) => setForm((p) => ({ ...p, max_uses: e.target.value }))}
                data-testid="promo-max-uses-field"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-body">Expires at (ISO datetime)</Label>
              <Input
                value={form.expires_at}
                onChange={(e) => setForm((p) => ({ ...p, expires_at: e.target.value }))}
                placeholder="2026-12-31T23:59:59Z"
                data-testid="promo-expires-field"
              />
            </div>
            <div className="flex items-center gap-3 sm:col-span-2">
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm((p) => ({ ...p, active: v }))}
                id="promo-active-toggle"
                data-testid="promo-active-toggle"
              />
              <Label htmlFor="promo-active-toggle" className="font-body text-sm">Active</Label>
            </div>
            {creating && (
              <div className="flex items-start gap-2 sm:col-span-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                <Checkbox
                  id="promo-broadcast-toggle"
                  checked={form.broadcast}
                  onCheckedChange={(v) => setForm((p) => ({ ...p, broadcast: v === true }))}
                  className="mt-0.5"
                  data-testid="promo-broadcast-toggle"
                />
                <div className="flex-1">
                  <Label htmlFor="promo-broadcast-toggle" className="font-body text-sm font-medium text-amber-900 cursor-pointer">Email this code to opt-in subscribers</Label>
                  <p className="text-xs text-amber-800/80 mt-0.5 font-body">Sends to every customer who has marketing-consent enabled and a verified email. You can also broadcast later from the table actions.</p>
                </div>
              </div>
            )}
            <div className="flex gap-2 sm:col-span-2">
              <Button onClick={handleSave} className="bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" data-testid="save-promo-button">
                <Save className="h-4 w-4 mr-1" /> Save
              </Button>
              <Button variant="outline" onClick={cancel} data-testid="cancel-promo-button"><X className="h-4 w-4 mr-1" /> Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
          ) : codes.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground italic">No promo codes yet — create your first one.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-heading">Code</th>
                    <th className="px-4 py-3 font-heading">Discount</th>
                    <th className="px-4 py-3 font-heading">Description</th>
                    <th className="px-4 py-3 font-heading text-center">Active</th>
                    <th className="px-4 py-3 font-heading">Used</th>
                    <th className="px-4 py-3 font-heading">Expires</th>
                    <th className="px-4 py-3 font-heading text-center">Reports</th>
                    <th className="px-4 py-3 font-heading text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((c) => {
                    const isSynth = c._synthetic;
                    return (
                      <tr key={c.code} className={`border-t border-border ${isSynth ? 'bg-amber-50/40' : ''}`} data-testid={`promo-row-${c.code}`}>
                        <td className="px-4 py-3 font-mono text-xs font-semibold">
                          {c.code}
                          {isSynth && <Badge className="ml-2 bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]">Auto</Badge>}
                        </td>
                        <td className="px-4 py-3">{c.discount_percent}%</td>
                        <td className="px-4 py-3 text-muted-foreground">{c.description || '—'}</td>
                        <td className="px-4 py-3 text-center">
                          {c.active ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge> : <Badge variant="outline">Inactive</Badge>}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">{c.times_used || 0}{c.max_uses ? ` / ${c.max_uses}` : ''}</td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}</td>
                        <td className="px-4 py-3 text-center">
                          <Button asChild size="sm" variant="ghost" data-testid={`report-promo-${c.code}`}>
                            <Link to={`/admin/promo-codes/${encodeURIComponent(c.code)}/report`}>
                              <BarChart3 className="h-3.5 w-3.5" />
                            </Link>
                          </Button>
                        </td>
                        <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                          {!isSynth && (
                            <>
                              <Button size="sm" variant="ghost" disabled={busyBroadcast === c.code} onClick={() => handleBroadcast(c.code)} title="Email this code to opt-in subscribers" data-testid={`broadcast-promo-${c.code}`}>
                                {busyBroadcast === c.code ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => startEdit(c)} data-testid={`edit-promo-${c.code}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                              <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(c.code)} data-testid={`delete-promo-${c.code}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </>
                          )}
                          {isSynth && <span className="text-xs text-muted-foreground italic">aggregate</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
